import { useMemo } from 'react';
import { CallIntelligenceWritebackV1, CallIntelTypes } from '@frontend/api-call-intel';
import { useAppFlagStore } from '@frontend/shared';
import { CALL_INTEL_PMS_WRITEBACK_FF } from '../constants';

interface UseCallIntelPmsSyncConfigProps {
  call?: Pick<CallIntelTypes.CallRecord, 'person' | 'locationId' | 'id'>;
  locationId?: string;
  fetchSyncStatus?: boolean;
  isDemo?: boolean;
}

const MAX_POLL_COUNT = 10;
const POLL_INTERVAL_MS = 10000; // 10 seconds

export const useCallIntelPmsSyncConfig = ({
  call,
  locationId,
  fetchSyncStatus = false,
  isDemo = false,
}: UseCallIntelPmsSyncConfigProps) => {
  const isContactPresent = !!call?.person?.id;
  const { getFeatureFlagValue } = useAppFlagStore();
  const selectedLocationIds = useMemo(
    () => [locationId || call?.locationId || ''].filter(Boolean),
    [locationId, call?.locationId]
  );

  const isPMSWritebackFFEnabled = getFeatureFlagValue({
    locationIds: selectedLocationIds,
    flagName: CALL_INTEL_PMS_WRITEBACK_FF,
  });

  const { data: summarySyncMode, isLoading: isLoadingSummarySyncMode } =
    CallIntelligenceWritebackV1.Queries.useGetWritebackCapabilitiesQuery({
      request: {
        locationIds: selectedLocationIds,
        isDemo,
      },
      options: {
        enabled: isPMSWritebackFFEnabled && selectedLocationIds.length > 0,
        select: (data) => {
          const isCapable = data.capabilities?.[0]?.capability?.isCapable;
          if (!isCapable) {
            return CallIntelligenceWritebackV1.Types.WritebackSettingMode.WRITEBACK_SETTING_MODE_OFF;
          }
          const writebackSetting = data.capabilities?.[0]?.settings?.find(
            ({ settingName }) =>
              settingName === CallIntelligenceWritebackV1.Types.WritebackSettingName.WRITEBACK_SETTING_CALL_SUMMARY
          );

          return writebackSetting?.mode;
        },
      },
    });

  const showSummarySync =
    isPMSWritebackFFEnabled &&
    (isDemo ||
      (summarySyncMode &&
        summarySyncMode !== CallIntelligenceWritebackV1.Types.WritebackSettingMode.WRITEBACK_SETTING_MODE_OFF));

  const {
    data: summarySyncStatusResponse,
    isLoading: isLoadingSummarySync,
    isFetching: isFetchingSummarySync,
  } = CallIntelligenceWritebackV1.Queries.useCallWritebackStatusQuery({
    request: {
      callId: call?.id,
      // @ts-expect-error - isDemo is used internally by demo interceptor
      isDemo,
    },
    options: {
      enabled: showSummarySync && fetchSyncStatus && !!call?.id,
      refetchInterval: (data, query) => {
        if (!data?.status) return false;

        const syncStatus = data.status;
        const isIndeterminateStatus = [
          CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_INPROGRESS,
          CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_TRIGGERED,
        ].includes(syncStatus);

        if (isIndeterminateStatus && query.state.dataUpdateCount <= MAX_POLL_COUNT) {
          return POLL_INTERVAL_MS; // Poll every N seconds
        }

        // Stop polling if terminal status or max attempts reached
        return false;
      },
      refetchIntervalInBackground: false,
    },
  });

  return {
    isLoadingSummarySyncMode,
    summarySyncMode,
    isContactPresent,
    showSummarySync,
    isLoadingSummarySync,
    isFetchingSummarySync,
    summarySyncStatusResponse,
  };
};
